import { attachDevCallerEmailInterceptor } from './attachDevCallerEmailInterceptor';

/**
 * API client for a Deacon app: every request goes to the same-origin
 * ``/api/<appName>`` path.
 *
 *   - Deployed, the SWA edge authenticates the caller and signs
 *     ``x-ms-client-principal``, and the app's SWA Function forwards the request
 *     to its pam-deacon App Service with the backend secret.
 *   - Under ``npm start``, the app's ``src/setupProxy.js`` forwards the same
 *     path to a local ``python -m apps.dev_server <app>``, which identifies the
 *     caller from ``X-Dev-Caller-Email`` (see attachDevCallerEmailInterceptor).
 *
 * The browser therefore never holds a backend credential in any environment.
 * There is no Beacon mode: Beacon apps stay on @1823-partners/core 1.x.
 *
 * @param {Object} config
 * @param {Object} config.axios   Axios instance (required).
 * @param {string} config.appName URL slug for the app, matching its SWA Function
 *                                route (e.g. ``"secmaster"`` -> ``/api/secmaster``).
 *
 * @returns {{get: Function, post: Function, put: Function, delete: Function,
 *            patch: Function, baseUrl: string}}
 *
 * @example
 *   import axios from 'axios';
 *   import { createAppTransport } from '@1823-partners/core';
 *
 *   const api = createAppTransport({ axios, appName: 'secmaster' });
 *   const { data } = await api.get('/secmaster-entries');
 */
export const createAppTransport = ({ axios, appName }) => {
  if (!axios) throw new Error('createAppTransport: axios instance is required');
  if (!appName) throw new Error('createAppTransport: appName is required');

  // Idempotent, and inert in production builds (see the interceptor).
  attachDevCallerEmailInterceptor(axios);

  const baseUrl = `/api/${appName}`;

  return {
    get: (endpoint, config = {}) => axios.get(`${baseUrl}${endpoint}`, config),
    post: (endpoint, data, config = {}) => axios.post(`${baseUrl}${endpoint}`, data, config),
    put: (endpoint, data, config = {}) => axios.put(`${baseUrl}${endpoint}`, data, config),
    delete: (endpoint, config = {}) => axios.delete(`${baseUrl}${endpoint}`, config),
    patch: (endpoint, data, config = {}) => axios.patch(`${baseUrl}${endpoint}`, data, config),
    baseUrl,
  };
};

export default createAppTransport;
